import { useState, useEffect } from "react";
import { Link, usePage } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  ShoppingCart,
  Menu,
  User,
  Search,
  Heart,
  Star,
  ChevronRight,
} from "lucide-react";
import Container from "./Container";
import { PageProps } from "../types";

const Header = ({
  storeName,
  storeLogo,
  storeSettings,
  exclusiveProducts: propExclusiveProducts,
  cart,
}: {
  storeName: string;
  storeLogo?: string;
  storeSettings?: any;
  exclusiveProducts?: any[];
  cart?: { items_count: number };
}) => {
  const { auth, exclusiveProducts: pageExclusiveProducts } =
    usePage<PageProps>().props;
  const exclusiveProducts = propExclusiveProducts || pageExclusiveProducts;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/", label: "Inicio", icon: "🏠" },
    { href: "/about", label: "Nosotros", icon: "👥" },
    { href: "/products", label: "Productos", icon: "🛍️" },
    { href: "/blog", label: "Blog", icon: "📝" },
  ];

  return (
    <div>
      <div className="bg-brand-gradient text-black text-center py-2 text-sm font-medium">
        <span className="animate-pulse">
          {storeSettings?.shipping_info || "🎉 Envío gratis en compras mayores a $50 🎉"}
        </span>
      </div>

      <header
        className={`sticky top-0 z-50 w-full transition-all duration-300 ${
          mounted && isScrolled
            ? "glass-effect shadow-elegant border-b border-neutral-200/50"
            : "bg-white shadow-lg border-b border-neutral-200"
        }`}
      >
        <Container>
          <div className="flex h-16 lg:h-20 items-center justify-between">
            {/* Logo - Moved to the left */}
            <Link href="/" className="flex items-center gap-3 group">
              {storeLogo && (
                <div className="relative">
                  <img
                    src={storeLogo}
                    alt={storeName}
                    className="h-8 lg:h-10 w-auto transition-transform group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-brand-gradient opacity-0 group-hover:opacity-10 rounded transition-opacity" />
                </div>
              )}
               {/* Store name now visible on mobile */}
                 <div className="flex flex-col items-center lg:items-start">
                 <span className="text-lg lg:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-slate-700 to-slate-500 dark:from-slate-200 dark:to-slate-400">
                   {storeName}
                 </span>
                 {storeSettings?.store_slogan && (
                   <span className="text-xs lg:text-sm text-neutral-600 dark:text-neutral-400 italic font-light mt-0.5">
                     {storeSettings.store_slogan}
                   </span>
                 )}
               </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="relative font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 transition-all duration-300 group py-2"
                >
                  {link.label}
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-brand-primary to-brand-secondary transition-all duration-300 group-hover:w-full" />
                </Link>
              ))}
              <DropdownMenu>
                <DropdownMenuTrigger className="relative font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 transition-all duration-300 group py-2 flex items-center gap-1">
                  <Star className="h-4 w-4 text-brand-secondary" />
                  Radios
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-brand-primary to-brand-secondary transition-all duration-300 group-hover:w-full" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="center"
                  className="w-64 glass-effect border border-neutral-200/50"
                >
                  <DropdownMenuLabel className="text-brand-secondary">
                    Radios
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {exclusiveProducts?.slice(0, 5).map((product) => (
                    <DropdownMenuItem key={product.id} asChild>
                      <Link
                        href={`/productos-exclusivos/${product.slug}`}
                        className="cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800"
                      >
                        {product.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-2 lg:gap-3">
              {/* Cart - Uncommented and always visible */}
              {/*
              <Link href="/cart">
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  <ShoppingCart className="h-5 w-5" />
                  {cart && cart.items_count > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-brand-primary text-black text-xs rounded-full flex items-center justify-center font-bold animate-glow-subtle">
                      {cart.items_count}
                    </span>
                  )}
                </Button>
              </Link>
              */}
              {/* Mobile Menu Button - Moved to the right */}
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  >
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                {/* Improved Mobile Menu Panel */}
                <SheetContent
                  side="right"
                  className="w-80 bg-white dark:bg-neutral-900 flex flex-col p-0"
                >
                  {/* Panel Header */}
                  <div className="flex items-center gap-3 p-4 border-b border-neutral-200 dark:border-neutral-800">
                    {storeLogo && (
                      <img
                        src={storeLogo}
                        alt={storeName}
                        className="h-9 w-auto rounded-md"
                      />
                    )}
                    <span className="text-lg font-bold text-neutral-800 dark:text-neutral-100">
                      {storeName}
                    </span>
                  </div>

                  {/* Scrollable Content Area */}
                  <div className="flex-grow overflow-y-auto">
                    {/* Main Navigation */}
                    <div className="py-4">
                      <h3 className="px-4 mb-2 text-xs font-semibold uppercase text-neutral-500 tracking-wider">
                        Menú
                      </h3>
                      <nav>
                        {navLinks.map((link) => (
                          <Link
                            key={link.href}
                            href={link.href}
                            className="flex items-center justify-between px-4 py-3 text-base font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <span>{link.label}</span>
                            <ChevronRight className="h-5 w-5 text-neutral-400" />
                          </Link>
                        ))}
                      </nav>
                    </div>

                    {/* Divider */}
                    <div className="px-4">
                      <div className="border-t border-neutral-200 dark:border-neutral-800" />
                    </div>

                    {/* Exclusive Products */}
                    {exclusiveProducts && exclusiveProducts.length > 0 && (
                      <div className="py-4">
                        <h3 className="px-4 mb-2 text-xs font-semibold uppercase text-neutral-500 tracking-wider">
                          Radios
                        </h3>
                        <div className="space-y-1">
                          {exclusiveProducts.slice(0, 4).map((product) => (
                            <Link
                              key={product.id}
                              href={`/productos-exclusivos/${product.slug}`}
                              className="block px-4 py-2 text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md mx-2"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {product.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Panel Footer */}
                  <div className="p-4 border-t border-neutral-200 dark:border-neutral-800">
                    <p className="text-xs text-center text-neutral-500">
                      {storeName} &copy; {new Date().getFullYear()}
                    </p>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </Container>
      </header>
    </div>
  );
};

export default Header;

